class EstimatedDelivery extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    const { signal } = (this.#abortController = new AbortController());
    const form = this.querySelector('[data-etd-form]');
    const input = this.querySelector('[data-etd-input]');

    form?.addEventListener('submit', this.#handleSubmit, { signal });

    const savedPincode = window.localStorage.getItem(this.#storageKey());
    if (input instanceof HTMLInputElement && savedPincode) {
      input.value = savedPincode;
      this.#showEstimate(savedPincode);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #storageKey() {
    return `etd-pincode-${this.dataset.blockId || 'default'}`;
  }

  /** @param {Event} event */
  #handleSubmit = (event) => {
    event.preventDefault();

    const input = this.querySelector('[data-etd-input]');
    const error = this.querySelector('[data-etd-error]');

    if (!(input instanceof HTMLInputElement)) return;

    const pincode = input.value.replace(/\D/g, '').slice(0, 6);
    input.value = pincode;

    if (!/^\d{6}$/.test(pincode)) {
      if (error) {
        error.textContent = this.dataset.invalidMessage || 'Please enter a valid 6-digit pincode.';
        error.hidden = false;
      }
      this.querySelector('[data-etd-result]')?.setAttribute('hidden', '');
      return;
    }

    if (error) error.hidden = true;
    window.localStorage.setItem(this.#storageKey(), pincode);
    this.#showEstimate(pincode);
  };

  /**
   * @param {string} pincode
   */
  #showEstimate(pincode) {
    const result = this.querySelector('[data-etd-result]');
    const resultText = this.querySelector('[data-etd-result-text]');

    if (!result || !resultText) return;

    const minDays = Number(this.dataset.deliveryDaysMin) || 3;
    const maxDays = Number(this.dataset.deliveryDaysMax) || 5;
    const locale = document.documentElement.lang || 'en-IN';

    const minDate = this.#addBusinessDays(new Date(), minDays);
    const maxDate = this.#addBusinessDays(new Date(), maxDays);

    const formatter = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const template = this.dataset.resultTemplate || 'Delivery to [pincode] by [date]';
    const dateText =
      minDays === maxDays
        ? formatter.format(maxDate)
        : `${formatter.format(minDate)} - ${formatter.format(maxDate)}`;

    resultText.textContent = template
      .replace(/\[pincode\]/g, pincode)
      .replace(/\[date\]/g, dateText)
      .replace(/\{\{\s*pincode\s*\}\}/g, pincode)
      .replace(/\{\{\s*date\s*\}\}/g, dateText);

    result.hidden = false;
  }

  /**
   * @param {Date} startDate
   * @param {number} days
   */
  #addBusinessDays(startDate, days) {
    const date = new Date(startDate);
    let added = 0;

    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) {
        added += 1;
      }
    }

    return date;
  }
}

if (!customElements.get('estimated-delivery')) {
  customElements.define('estimated-delivery', EstimatedDelivery);
}
